import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { Button } from '@/components/ui/Button'
import { Page, PageHeader } from '@/components/ui/Page'
import { ErrorState, Loading } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { COLOR_LABEL, COLOR_NAMES, boardBackground, type ColorName } from '@/lib/colors'
import { formatDateLong, today } from '@/lib/dates'
import { describeError } from '@/lib/supabase'
import { TODO_LISTS, arrangeTodos, sortOrderAt, type TodoList } from '@/lib/todos'
import { useAddTodo, useDeleteTodos, useSetBoardColor, useTodoBoards, useTodos, useUpdateTodo, type Todo } from './queries'

type Columns = Record<TodoList, string[]>

const LIST_KEYS = TODO_LISTS.map((list) => list.key)

function isList(id: string): id is TodoList {
  return (LIST_KEYS as string[]).includes(id)
}

function emptyColumns(): Columns {
  return Object.fromEntries(LIST_KEYS.map((key) => [key, []])) as unknown as Columns
}

const BUSINESS: TodoList[] = ['today', 'week', 'ideas', 'crew']
const PERSONAL: TodoList[] = ['personal']

/** Operations > To do: the business boards. */
export function HqPage() {
  return <TodoBoardsPage lists={BUSINESS} eyebrow="Operations" />
}

/** Personal HQ > To do: life outside the business, kept apart from it. */
export function PersonalPage() {
  return <TodoBoardsPage lists={PERSONAL} eyebrow="Personal HQ" />
}

/**
 * To do: Jena's own boards, Trello style (27 Oct 2026). Operations has Today,
 * This week, Ideas, and Associate / second shooters still to book; Personal
 * HQ has her personal list. Side by side, scrolling sideways when they don't
 * fit. Each board's menu (⋯) sets its
 * background colour; the cards stay white. Cards drag within a board and from
 * one board to another. Ticked cards sink to the bottom until cleared.
 *
 * Each person sees only their own boards (RLS on todos and todo_boards).
 */
function TodoBoardsPage({ lists, eyebrow }: { lists: TodoList[]; eyebrow: string }) {
  const query = useTodos()
  const boards = useTodoBoards()
  const update = useUpdateTodo()
  const [preview, setPreview] = useState<Columns | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    // A few pixels of movement before a drag starts, so a click still ticks
    // the box or opens the text.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const byId = useMemo(() => new Map((query.data ?? []).map((todo) => [todo.id, todo])), [query.data])

  // Open cards per board, in order: what can be dragged.
  const stored = useMemo<Columns>(() => {
    const columns = emptyColumns()
    for (const todo of arrangeTodos(query.data ?? [])) {
      if (!todo.done_at) columns[todo.list]?.push(todo.id)
    }
    return columns
  }, [query.data])

  const columns = preview ?? stored

  function containerOf(id: string, from: Columns): TodoList | null {
    if (isList(id)) return id
    return LIST_KEYS.find((key) => from[key].includes(id)) ?? null
  }

  function onDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id))
    setPreview(stored)
  }

  // Crossing into another board: show it there straight away.
  function onDragOver(event: DragOverEvent) {
    const { active, over } = event
    if (!over) return
    setPreview((current) => {
      const base = current ?? stored
      const id = String(active.id)
      const from = containerOf(id, base)
      const to = containerOf(String(over.id), base)
      if (!from || !to || from === to) return base
      const target = base[to]
      const overIndex = isList(String(over.id)) ? target.length : target.indexOf(String(over.id))
      return {
        ...base,
        [from]: base[from].filter((item) => item !== id),
        [to]: [...target.slice(0, overIndex), id, ...target.slice(overIndex)],
      }
    })
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event
    const base = preview ?? stored
    setActiveId(null)
    setPreview(null)
    if (!over) return

    const id = String(active.id)
    const to = containerOf(id, base)
    if (!to) return
    let ids = base[to]
    if (!isList(String(over.id)) && over.id !== id && ids.includes(String(over.id))) {
      ids = arrayMove(ids, ids.indexOf(id), ids.indexOf(String(over.id)))
    }
    const index = ids.indexOf(id)
    const others = ids.filter((item) => item !== id).map((item) => byId.get(item)).filter((todo): todo is Todo => Boolean(todo))
    const todo = byId.get(id)
    if (!todo) return
    if (todo.list === to && ids.join() === stored[to].join()) return
    update.mutate({ id, patch: { list: to, sort_order: sortOrderAt(others, index) } })
  }

  if (query.isPending) return <Loading label="Loading your boards" />

  if (query.isError) {
    return (
      <Page>
        <ErrorState title="Your boards could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      </Page>
    )
  }

  const active = activeId ? byId.get(activeId) : undefined

  return (
    <Page className="max-w-none">
      <PageHeader eyebrow={eyebrow} title="To do" meta={<time dateTime={today()}>{formatDateLong(today())}</time>} />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={() => {
          setActiveId(null)
          setPreview(null)
        }}
      >
        <div className="-mx-5 overflow-x-auto px-5 pb-3 lg:-mx-8 lg:px-8">
          <div className="flex w-max items-start gap-3">
            {TODO_LISTS.filter((list) => lists.includes(list.key)).map((list) => (
              <Board
                key={list.key}
                list={list.key}
                title={list.title}
                empty={list.empty}
                color={boards.data?.[list.key] ?? null}
                openIds={columns[list.key]}
                done={(query.data ?? []).filter((todo) => todo.list === list.key && todo.done_at)}
                byId={byId}
              />
            ))}
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {active ? (
            <div className="card w-[17.5rem] rotate-2 px-3 py-2 text-[length:var(--text-sm)] shadow-[var(--shadow-menu)]">{active.title}</div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </Page>
  )
}

function Board({
  list,
  title,
  empty,
  color,
  openIds,
  done,
  byId,
}: {
  list: TodoList
  title: string
  empty: string
  color: string | null
  openIds: string[]
  done: Todo[]
  byId: Map<string, Todo>
}) {
  const { setNodeRef } = useDroppable({ id: list })
  const add = useAddTodo()
  const remove = useDeleteTodos()
  const [adding, setAdding] = useState(false)
  const [text, setText] = useState('')
  const open = openIds.map((id) => byId.get(id)).filter((todo): todo is Todo => Boolean(todo))
  const sortedDone = arrangeTodos(done)

  function submit() {
    const title = text.trim()
    if (!title) return
    add.mutate({ id: crypto.randomUUID(), list, title, sort_order: sortOrderAt(open, open.length) })
    setText('')
  }

  return (
    <section aria-label={title} className="flex w-[18.5rem] shrink-0 flex-col rounded-xl p-2" style={{ background: boardBackground(color) }}>
      <header className="flex items-center justify-between gap-2 px-2 pt-0.5 pb-2">
        <h2 className="text-[length:var(--text-sm)] font-semibold">{title}</h2>
        <div className="flex items-center gap-1">
          <span className="text-[length:var(--text-xs)] text-taupe" data-numeric>
            {open.length}
          </span>
          <BoardMenu list={list} title={title} color={color} doneIds={sortedDone.map((todo) => todo.id)} onClear={(ids) => remove.mutate(ids)} />
        </div>
      </header>

      <SortableContext id={list} items={openIds} strategy={verticalListSortingStrategy}>
        <ul ref={setNodeRef} className="flex min-h-[2.5rem] flex-col gap-2">
          {open.map((todo) => (
            <SortableCard key={todo.id} todo={todo} />
          ))}
          {open.length === 0 && !adding ? <li className="px-2 py-1 text-[length:var(--text-xs)] text-taupe">{empty}</li> : null}
          {sortedDone.map((todo) => (
            <CardRow key={todo.id} todo={todo} />
          ))}
        </ul>
      </SortableContext>

      {adding ? (
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <textarea
            autoFocus
            rows={2}
            aria-label={`Add to ${title}`}
            placeholder="Enter a title for this card…"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                submit()
              }
              if (event.key === 'Escape') {
                setAdding(false)
                setText('')
              }
            }}
            className="card resize-none px-3 py-2 text-[length:var(--text-sm)] outline-none focus:ring-2 focus:ring-accent"
          />
          <div className="flex items-center gap-2">
            <Button type="submit" variant="primary" size="sm" disabled={!text.trim()}>
              Add card
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Stop adding"
              onClick={() => {
                setAdding(false)
                setText('')
              }}
            >
              ✕
            </Button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[length:var(--text-sm)] text-taupe hover:bg-[var(--color-wash-strong)] hover:text-ink"
        >
          <span aria-hidden className="text-base leading-none">+</span> Add a card
        </button>
      )}
    </section>
  )
}

/** The ⋯ menu on a board: its background colour, and clearing ticked cards. */
function BoardMenu({
  list,
  title,
  color,
  doneIds,
  onClear,
}: {
  list: TodoList
  title: string
  color: string | null
  doneIds: string[]
  onClear: (ids: string[]) => void
}) {
  const setColor = useSetBoardColor()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(event: MouseEvent) {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-label={`${title} menu`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="grid size-7 place-items-center rounded-md text-taupe hover:bg-[var(--color-wash-strong)] hover:text-ink"
      >
        <svg width="14" height="4" viewBox="0 0 14 4" aria-hidden>
          <circle cx="2" cy="2" r="1.5" fill="currentColor" />
          <circle cx="7" cy="2" r="1.5" fill="currentColor" />
          <circle cx="12" cy="2" r="1.5" fill="currentColor" />
        </svg>
      </button>
      {open ? (
        <div className="absolute right-0 z-30 mt-1 w-64 rounded-lg bg-[var(--surface-card)] p-3 shadow-[var(--shadow-menu)]">
          <p className="mb-2 text-center text-[length:var(--text-sm)] font-semibold">{title}</p>
          <p className="label mb-1.5">Board colour</p>
          <div className="grid grid-cols-6 gap-1.5" role="radiogroup" aria-label="Board colour">
            <Swatch label="Default" background="var(--color-paper)" chosen={!color} onPick={() => setColor.mutate({ list, color: null })} />
            {COLOR_NAMES.map((key: ColorName) => (
              <Swatch
                key={key}
                label={COLOR_LABEL[key]}
                background={`var(--board-${key})`}
                chosen={color === key}
                onPick={() => setColor.mutate({ list, color: key })}
              />
            ))}
          </div>
          {doneIds.length > 0 ? (
            <Button
              size="sm"
              className="mt-3 w-full"
              onClick={() => {
                onClear(doneIds)
                setOpen(false)
              }}
            >
              Clear {doneIds.length} ticked {doneIds.length === 1 ? 'card' : 'cards'}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function Swatch({ label, background, chosen, onPick }: { label: string; background: string; chosen: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={chosen}
      aria-label={label}
      title={label}
      onClick={onPick}
      className={cn('h-8 rounded-md border', chosen && 'ring-2 ring-accent ring-offset-2 ring-offset-[var(--surface-card)]')}
      style={{ background }}
    />
  )
}

function SortableCard({ todo }: { todo: Todo }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: todo.id })
  return (
    <CardRow
      todo={todo}
      rowRef={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      dragging={isDragging}
      dragProps={{ ...attributes, ...listeners }}
    />
  )
}

function CardRow({
  todo,
  rowRef,
  style,
  dragging = false,
  dragProps,
}: {
  todo: Todo
  rowRef?: (node: HTMLElement | null) => void
  style?: React.CSSProperties
  dragging?: boolean
  dragProps?: React.HTMLAttributes<HTMLElement>
}) {
  const update = useUpdateTodo()
  const remove = useDeleteTodos()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(todo.title)

  function save() {
    setEditing(false)
    const title = draft.trim()
    if (title && title !== todo.title) update.mutate({ id: todo.id, patch: { title } })
    else setDraft(todo.title)
  }

  return (
    <li
      ref={rowRef}
      style={style}
      {...(editing ? {} : dragProps)}
      className={cn(
        'card group flex items-start gap-2 px-3 py-2 hover:ring-2 hover:ring-accent',
        dragProps && !editing && 'cursor-grab touch-none',
        dragging && 'opacity-30',
        todo.done_at && 'opacity-60',
      )}
    >
      <input
        type="checkbox"
        aria-label={todo.done_at ? `Untick ${todo.title}` : `Tick ${todo.title}`}
        checked={Boolean(todo.done_at)}
        onChange={(event) => update.mutate({ id: todo.id, patch: { done_at: event.target.checked ? new Date().toISOString() : null } })}
        className="check mt-0.5 size-4 shrink-0"
      />
      {editing ? (
        <textarea
          autoFocus
          rows={2}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={save}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              save()
            }
            if (event.key === 'Escape') {
              setDraft(todo.title)
              setEditing(false)
            }
          }}
          className="min-w-0 flex-1 resize-none bg-transparent text-[length:var(--text-sm)] outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setDraft(todo.title)
            setEditing(true)
          }}
          className={cn('min-w-0 flex-1 cursor-text text-left text-[length:var(--text-sm)] leading-snug break-words', todo.done_at && 'line-through')}
        >
          {todo.title}
        </button>
      )}
      <button
        type="button"
        aria-label={`Remove ${todo.title}`}
        onClick={() => remove.mutate([todo.id])}
        className="rounded px-1 text-taupe opacity-0 group-hover:opacity-100 hover:text-clay focus:opacity-100"
      >
        ✕
      </button>
    </li>
  )
}
